import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { verifyBuyPaymentApi } from "../../api/apiServices";
import jsPDF from "jspdf"; // npm install jspdf

export default function BuyCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [paystackData, setPaystackData] = useState(null);

  const reference = searchParams.get("reference") || searchParams.get("ref");

  useEffect(() => {
    if (!reference) {
      setStatus("error");
      setMessage("No transaction reference found");
      return;
    }

    const verifyPayment = async () => {
      try {
        const data = await verifyBuyPaymentApi(reference);

        if (data.status === true && data.data?.status === "success") {
          setStatus("success");
          setTransaction(data.data);
          setPaystackData(data.data);
          setMessage("Payment successful! Your crypto has been credited.");
        } else {
          setStatus("failed");
          setMessage("Payment was not successful.");
        }
      } catch (err) {
        console.error(err);
        setStatus("error");
        setMessage("Unable to verify payment. Please contact support.");
      }
    };

    verifyPayment();
  }, [reference]);

  // Download Receipt as PDF
  const downloadReceipt = () => {
    if (!paystackData) return;

    const doc = new jsPDF();
    const date = new Date().toLocaleDateString("en-NG");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Nivora - Payment Receipt", 105, 20, { align: "center" });

    doc.setFontSize(11);
    doc.text(`Transaction Reference: ${paystackData.reference}`, 20, 40);
    doc.text(`Date: ${date}`, 20, 48);
    doc.text(`Payment Method: ${paystackData.channel?.toUpperCase()}`, 20, 56);

    doc.text("Asset Purchased:", 20, 70);
    doc.setFont("helvetica", "normal");
    doc.text(`${paystackData.metadata?.asset} (${paystackData.metadata?.asset})`, 20, 78);

    doc.text(`Amount Paid: ₦${(paystackData.amount / 100).toLocaleString()}`, 20, 90);
    doc.text(`You Received: ${paystackData.metadata?.cryptoAmount || "N/A"} ${paystackData.metadata?.asset}`, 20, 98);

    doc.setFont("helvetica", "bold");
    doc.text("Status: SUCCESSFUL", 20, 115);
    doc.setFont("helvetica", "normal");
    doc.text("Thank you for using Nivora!", 20, 130);

    doc.save(`Nivora_Receipt_${paystackData.reference}.pdf`);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28 flex flex-col items-center justify-center px-5 text-center">
      {status === "verifying" && (
        <>
          <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-6"></div>
          <h2 className="text-xl font-bold mb-2">Verifying Payment</h2>
          <p className="text-slate-500">Please wait while we confirm your transaction...</p>
        </>
      )}

      {status === "success" && paystackData && (
        <>
          <div className="w-24 h-24 rounded-full bg-emerald-100 border-4 border-emerald-200 flex items-center justify-center mb-6">
            <i className="ri-check-line text-emerald-600 text-6xl"></i>
          </div>

          <h2 className="text-3xl font-bold text-emerald-600 mb-1">Payment Successful!</h2>
          <p className="text-slate-600 mb-8 max-w-xs">{message}</p>

          <div className="bg-white rounded-3xl p-6 w-full max-w-md mb-8 shadow-sm">
            <div className="text-left space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-500">Asset</span>
                <span className="font-semibold">{paystackData.metadata?.asset}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount Paid</span>
                <span className="font-semibold">₦{(paystackData.amount / 100).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">You Received</span>
                <span className="font-bold text-emerald-600">
                  {paystackData.metadata?.cryptoAmount || "N/A"} {paystackData.metadata?.asset}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Reference</span>
                <span className="font-mono text-xs">{paystackData.reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date</span>
                <span>{new Date(paystackData.paid_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full max-w-xs">
            <button
              onClick={downloadReceipt}
              className="w-full py-4 bg-white border border-emerald-600 text-emerald-600 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-50"
            >
              <i className="ri-download-line"></i> Download Receipt
            </button>

            <button
              onClick={() => navigate("/home")}
              className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-semibold"
            >
              Back to Home
            </button>

            <button
              onClick={() => navigate("/transactions")}
              className="text-emerald-600 text-sm font-medium"
            >
              View Transaction History
            </button>
          </div>
        </>
      )}

      {(status === "failed" || status === "error") && (
        <>
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <i className="ri-close-line text-red-600 text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold mb-2 text-red-600">Payment Failed</h2>
          <p className="text-slate-600 mb-8 max-w-xs">{message}</p>
          <button
            onClick={() => navigate("/buy")}
            className="w-full max-w-xs py-4 bg-slate-800 text-white rounded-2xl font-semibold"
          >
            Try Again
          </button>
        </>
      )}

      <MobileBottomNav />
    </div>
  );
}